import { useContentChannelStore } from '@/context/useContentChannelStore'
import useExistingBookingStore from '@/context/useExistingBookingStore'
import { getEntryUtm } from '@/libs/analytics/entryUtm'
type AnalyticsEventParams = Record<string, any>

declare global {
  interface Window {
    gtag?: (...args: any[]) => void
    dataLayer?: any[]
  }
}

export function hasCookieConsent(): boolean {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}')
    return !!user.cookies
  } catch {
    return false
  }
}

/**
 * Initialize Google Analytics with the measurement ID from environment variables
 *
 * Runs at boot (index.tsx), before consent is known. Consent Mode v2 keeps the
 * tag compliant: analytics_storage defaults to denied, so pre-consent activity
 * is sent as cookieless pings (no identifiers, modelled in reports) instead of
 * being dropped entirely. updateAnalyticsConsent() flips storage on when the
 * user accepts. Previously the tag only loaded after acceptance, which made
 * every pre-consent user invisible and re-attributed the rest to (direct).
 */
export function initGA() {
  const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID
  if (!measurementId || window.gtag) return

  window.dataLayer = window.dataLayer || []
  window.gtag = function () {
    window.dataLayer!.push(arguments)
  }

  // Consent defaults must be queued before the tag script evaluates.
  window.gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: hasCookieConsent() ? 'granted' : 'denied',
  })

  window.gtag('js', new Date())

  // Replay the entry URL's utm_* into the config: the auth flow redirects away
  // from the tagged landing URL (auth/verify) before the first page_view fires,
  // so campaign attribution must come from the boot-time snapshot, not the URL.
  const utm = getEntryUtm()
  const campaign: Record<string, string> = {}
  if (utm.utm_source) campaign.campaign_source = utm.utm_source
  if (utm.utm_medium) campaign.campaign_medium = utm.utm_medium
  if (utm.utm_campaign) campaign.campaign_name = utm.utm_campaign
  if (utm.utm_content) campaign.campaign_content = utm.utm_content
  if (utm.utm_term) campaign.campaign_term = utm.utm_term

  window.gtag('config', measurementId, {
    send_page_view: false,
    transport_url: process.env.REACT_APP_GA_TAG_URL,
    ...campaign,
  })

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`
  document.head.appendChild(script)

  console.log(`GA: Initialized with ${measurementId}`)
}

/**
 * Sync gtag's consent state with the user's stored cookie choice
 */
export function updateAnalyticsConsent(granted: boolean) {
  if (!window.gtag) return
  window.gtag('consent', 'update', {
    analytics_storage: granted ? 'granted' : 'denied',
  })
}

/**
 * Remove GA and Hotjar identifiers left behind after consent is withdrawn
 */
export function clearAnalyticsStorage() {
  try {
    localStorage.removeItem('ga_client_id')
  } catch {
    // localStorage can be unavailable (private mode); nothing to clear.
  }

  // Tags may set cookies on the host or any parent domain, so expire on each.
  const labels = window.location.hostname.split('.')
  const domains = labels.map((_, i) => labels.slice(i).join('.'))
  document.cookie
    .split(';')
    .map((cookie) => cookie.split('=')[0].trim())
    .filter((name) => name.startsWith('_ga') || name.startsWith('_hj'))
    .forEach((name) => {
      const expired = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
      document.cookie = expired
      domains.forEach((domain) => {
        document.cookie = `${expired}; domain=.${domain}`
      })
    })
}

/**
 * Track custom events with automatic event_uid injection
 */
export function trackEvent(eventName: string, params?: AnalyticsEventParams) {
  const { booking } = useExistingBookingStore.getState()

  if (!window.gtag) {
    console.warn('GA: Not initialized, skipping event:', eventName)
    return
  }

  try {
    const { channelContent } = useContentChannelStore.getState()
    // A showflow has no booking; the console's rollup still needs its channel_uid
    const showflow = channelContent?.is_showflow
      ? { channel_uid: channelContent.uid, is_showflow: true }
      : {}
    // channel_uid defaults to the active booking, but an explicit channel_uid in
    // params wins — callers that already know the channel (e.g. a wallet ticket
    // view) can tag it correctly without depending on store timing.
    window.gtag('event', eventName, {
      channel_uid: booking?.channel_uid,
      ...showflow,
      ...params,
      timestamp: Date.now(),
    })
    console.log(`GA: Event - ${eventName}`, {
      channelUid: booking?.channel_uid,
      ...showflow,
      ...params,
    })
  } catch (err) {
    console.error('GA: Error tracking event:', err)
  }
}
